/**
 * Created by Christiaan on 13/03/2017.
 */
import {ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Router} from "@angular/router";
import {RequestFailure} from "../../../shared/services/http/request-failure";
import {UntypedFormGroup, UntypedFormBuilder, Validators} from '@angular/forms';
import {HTTPService} from "../../../shared/services/http/http.service";
import {AppSettings} from "../../../app.settings";
import {UserService} from "../user.service";
import {TranslateService} from "../../../shared/services/translate/translate.service";
import {Subscription} from "rxjs";
import {GlobalModel} from "../../../shared/services/state/global.model";
import {LuminizerRoutes} from '../../../shared/interfaces/routes';
import {User} from "../user";
import {LoginService} from "../login.service";
import {LoggerService} from "../../../shared/services/logger/logger.service";
import {environment} from "../../../../environments/environment";

@Component ({
    selector: 'password-forget-component',
    templateUrl: 'password-forget.component.html'
})
export class PasswordForgetComponent implements OnInit, OnDestroy{

    @ViewChild('username', {static: false}) usernameInput:any;

    public passwordChangeForm:UntypedFormGroup;
    public failure:RequestFailure;
    private subChangeLanguage:Subscription;
    public flippingLanguage:boolean = false;
    private checkingLoginStatus:boolean = false;
    private firstTimeVisible:boolean = true;
    public _user:User = null;
    
    public formTitle:string = "";
    public formSubTitle:string = "";
    public formUsername:string = "";
    public formSubmit:string = "";
    public formLogin:string = "";

    public disableSubmitButton:boolean = true;
    private subUsernameChanges:Subscription;

    public turnstileSiteKey:string = environment.turnstileSiteKey;
    private captchaToken:string = null;

    constructor(public httpService:HTTPService, private loginService:LoginService, private formBuilder:UntypedFormBuilder, private model:GlobalModel, private router:Router, private userService:UserService, private ts:TranslateService, private cd:ChangeDetectorRef, protected logger:LoggerService)
    {
        this.createForm();
    }

    ngOnInit(){
        this.subChangeLanguage = this.model.changeLanguage.subscribe( () =>
        {
            this.formTitle = this.ts.translate('Wachtwoord vergeten');
            this.formSubTitle = this.ts.translate('passwordforget.title');
            this.formUsername = this.ts.translate('Gebruikersnaam');
            this.formSubmit = this.ts.translate('passwordforget.submit');
            this.formLogin = this.ts.translate('passwordforget.login');

            this.cd.detectChanges();
        })

        // Enable button only when the username field has input and the captcha is resolved
        this.subUsernameChanges = this.passwordChangeForm.get('username').valueChanges.subscribe((value:string) => {
            this.disableSubmitButton = this.isSubmitButtonDisabled(value);
            this.cd.detectChanges();
        });

    }

    public getErrorText(message:string):string{
        switch(message){
            case 'Invalid credentials.':
                return this.ts.translate('login.invalid');
            default:
                return message;
        }
    }

    public getErrorLabel(message:string):string{
        switch(message){
            case 'Let op:':
                return this.ts.translate('login.invalid-title');
            default:
                return message;
        }
    }

    getLogoPath(): string {
        if(this.model.isXmasMode()){
            return 'assets/img/luminizerlogo-xmas.png';
        } else {
            return '/assets/img/luminizerlogo.png';
        }
    }

    public handleFlippingLanguage(flippingLanguage:boolean){
        this.flippingLanguage = flippingLanguage;
    }

    ngOnDestroy(): void
    {
        if (this.subChangeLanguage){
            this.subChangeLanguage.unsubscribe();
        }
        if (this.subUsernameChanges){
            this.subUsernameChanges.unsubscribe();
        }
    }

    public isLoginVisible(user:User):boolean{

        if (!this.checkingLoginStatus && !this.loginService.isLoggedInLocally(user) && !this.flippingLanguage){

            //TODO: iets mooiers maken hiervoor. Door de animatie die in het loginscherm zit is het element niet beschikbaar
            //Daarvoor wordt die timer ingezet
            if (this.firstTimeVisible){
                this.firstTimeVisible = false;

                setTimeout(() => {
                    if (this.usernameInput) {
                        this.usernameInput.nativeElement.focus();
                    }
                }, 500);
            }

            return true;
        }else{
            return false;
        }
    }

    private createForm():void
    {
        this.passwordChangeForm = this.formBuilder.group({
            username: ['', [Validators.required, Validators.email]]
        })
    }

    public handleClickSend(event:MouseEvent)
    {
        this.failure = null;

        const usernameControl = this.passwordChangeForm.get('username');
        usernameControl.markAsTouched();

        const usernameValue = String(usernameControl.value ?? '').trim();

        if (usernameValue.length === 0) {
            this.showValidationFailure(this.ts.translate('passwordforget.username.required'));
            return;
        }

        if (usernameControl.hasError('email')) {
            this.showValidationFailure(this.ts.translate('passwordforget.username.invalid'));
            return;
        }

        if (!this.captchaToken) {
            this.showValidationFailure(this.ts.translate('turnstile.required'));
            return;
        }

        this.disableSubmitButton = true;

        this.userService.requestPasswordChange(usernameValue,
            this.captchaToken,
            () => {
                this.logger.log("[PasswordForgetComponent] " + "Change request succes");

                //TODO: dit moet als response uit de call komen
                const info:RequestFailure = new RequestFailure();
                info.title = this.ts.translate("passwordforget.emailsend.title");
                info.message = this.ts.translate("passwordforget.emailsend.message"); //"Volg de link in de e-mail om uw wachtwoord aan te passen.";
                this.failure = info;
                this.disableSubmitButton = true;
                this.cd.detectChanges();

            },
            (failure:RequestFailure) => {

                //App won't end up here. There is no failure for get-token or login
                this.logger.log("[PasswordForgetComponent] " + "Login failed" + failure);

                this.captchaToken = null; //single-use token, force a re-challenge
                this.usernameInput.nativeElement.focus();
                this.failure = failure;
                this.disableSubmitButton = true;
                this.cd.detectChanges();

            }
        );
    }

    private isSubmitButtonDisabled(username?:string): boolean
    {
        const usernameValue = username ?? this.passwordChangeForm?.get('username')?.value;
        return !usernameValue || String(usernameValue).trim().length === 0 || !this.captchaToken;
    }

    private showValidationFailure(message:string): void
    {
        let failure:RequestFailure = new RequestFailure();
        failure.severity = "warning";
        failure.title = this.ts.translate('passwordforget.warning.title');
        failure.message = message;
        this.failure = failure;
        this.disableSubmitButton = true;
        this.cd.detectChanges();
    }

    public onTurnstileResolved(token:string):void
    {
        this.captchaToken = token;
        this.disableSubmitButton = this.isSubmitButtonDisabled();
        this.cd.detectChanges();
    }
    
    public handleCancel(event:MouseEvent)
    {
        this.router.navigate([LuminizerRoutes.LOGIN_PAGE]);
    }
    
    public handleKeyDown($event: any) {
        if ($event.keyCode === 13) {
            $event.preventDefault();
            this.handleClickSend($event);
        }
    }
}
